import { useRef } from 'react';

import {
    DEFAULT_CURVE,
    formatCubicEase,
    parseCubicEase,
    sampleCubic,
} from '../../features/animations/ease';

const SIZE = 140;
const PAD = 14;
const Y_MIN = -0.5;
const Y_MAX = 1.5;

const PRESETS = [
    { label: 'Linear', curve: [0, 0, 1, 1] },
    { label: 'In', curve: [0.42, 0, 1, 1] },
    { label: 'Out', curve: [0, 0, 0.58, 1] },
    { label: 'In out', curve: DEFAULT_CURVE },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toSvg = (x, y) => {
    const span = SIZE - PAD * 2;
    return [
        PAD + x * span,
        PAD + (1 - (y - Y_MIN) / (Y_MAX - Y_MIN)) * span,
    ];
};

const fromSvg = (clientX, clientY, svg) => {
    const rect = svg.getBoundingClientRect();
    const span = SIZE - PAD * 2;
    const x = ((clientX - rect.left) / rect.width) * SIZE;
    const y = ((clientY - rect.top) / rect.height) * SIZE;
    return {
        x: clamp((x - PAD) / span, 0, 1),
        y: clamp(Y_MIN + (1 - (y - PAD) / span) * (Y_MAX - Y_MIN), Y_MIN, Y_MAX),
    };
};

const curvePath = (curve) => {
    const commands = [];
    for (let step = 0; step <= 28; step += 1) {
        const amount = step / 28;
        const [x, y] = toSvg(amount, sampleCubic(amount, curve[0], curve[1], curve[2], curve[3]));
        commands.push(`${step === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return commands.join(' ');
};

export default function CurveEditor({ ease, onChange }) {
    const svgRef = useRef(null);
    const curve = parseCubicEase(ease) || DEFAULT_CURVE;
    const [startX, startY] = toSvg(0, 0);
    const [endX, endY] = toSvg(1, 1);
    const [x1, y1] = toSvg(curve[0], curve[1]);
    const [x2, y2] = toSvg(curve[2], curve[3]);
    const [zeroX, zeroY] = toSvg(0, 0);
    const [oneX, oneY] = toSvg(1, 1);

    const dragHandle = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        const svg = svgRef.current;
        const move = (moveEvent) => {
            const point = fromSvg(moveEvent.clientX, moveEvent.clientY, svg);
            const next = [...curve];
            next[index * 2] = point.x;
            next[index * 2 + 1] = point.y;
            onChange(formatCubicEase(next[0], next[1], next[2], next[3]));
        };
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    return (
        <div className="anim-editor__curve">
            <svg
                ref={svgRef}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="anim-editor__curve-graph"
                aria-label="Custom ease curve"
            >
                <line x1={zeroX} y1={PAD} x2={zeroX} y2={SIZE - PAD} className="anim-editor__curve-axis" />
                <line x1={PAD} y1={zeroY} x2={SIZE - PAD} y2={zeroY} className="anim-editor__curve-axis" />
                <line x1={oneX} y1={PAD} x2={oneX} y2={SIZE - PAD} className="anim-editor__curve-guide" />
                <line x1={PAD} y1={oneY} x2={SIZE - PAD} y2={oneY} className="anim-editor__curve-guide" />
                <line x1={startX} y1={startY} x2={endX} y2={endY} className="anim-editor__curve-guide" />
                <path d={curvePath(curve)} className="anim-editor__curve-line" />
                <line x1={startX} y1={startY} x2={x1} y2={y1} className="anim-editor__curve-handle-line" />
                <line x1={endX} y1={endY} x2={x2} y2={y2} className="anim-editor__curve-handle-line" />
                <circle cx={x1} cy={y1} r="5" className="anim-editor__curve-handle" onPointerDown={(event) => dragHandle(event, 0)} />
                <circle cx={x2} cy={y2} r="5" className="anim-editor__curve-handle" onPointerDown={(event) => dragHandle(event, 1)} />
            </svg>
            <div className="anim-editor__curve-presets">
                {PRESETS.map((preset) => (
                    <button
                        key={preset.label}
                        type="button"
                        onClick={() => onChange(formatCubicEase(...preset.curve))}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
